import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Composant de sélection de pays avec indicatif téléphonique
 * Affiche drapeau + nom pays + indicatif
 */
const CountryPhoneSelect = ({ 
  phoneValue = '', 
  countryCode = 'FR',
  onPhoneChange,
  onCountryChange,
  label = 'Téléphone',
  required = false,
  disabled = false
}) => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Charger la liste des pays au montage
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/countries`);
        setCountries(response.data.countries || []);
        
        // Sélectionner le pays par défaut
        const defaultCountry = response.data.countries.find(
          c => c.country_code === countryCode
        );
        if (defaultCountry) {
          setSelectedCountry(defaultCountry);
        }
        setLoading(false);
      } catch {
        setLoading(false);
      }
    };

    fetchCountries();
  }, [countryCode]);

  // Filtrer les pays selon la recherche
  const filteredCountries = searchQuery
    ? countries.filter(country => 
        country.country_name_fr.toLowerCase().includes(searchQuery.toLowerCase()) ||
        country.dial_code.includes(searchQuery) ||
        country.country_code.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : countries;

  const handleCountryChange = (newCountryCode) => {
    const country = countries.find(c => c.country_code === newCountryCode);
    if (country) {
      setSelectedCountry(country);
      if (onCountryChange) {
        onCountryChange(country);
      }
    }
  };

  const handlePhoneChange = (e) => {
    if (onPhoneChange) {
      onPhoneChange(e.target.value);
    }
  };

  if (loading) {
    return (
      <div>
        <Label>{label}</Label>
        <div className="text-sm text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="flex gap-2">
        {/* Sélecteur de pays */}
        <div className="w-[200px]">
          <Select
            value={selectedCountry?.country_code || 'FR'}
            onValueChange={handleCountryChange}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue>
                {selectedCountry ? (
                  <span className="flex items-center gap-2">
                    <span>{selectedCountry.flag_emoji}</span>
                    <span>{selectedCountry.dial_code}</span>
                  </span>
                ) : (
                  'Sélectionner'
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <div className="p-2">
                <Input
                  placeholder="Rechercher un pays..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mb-2"
                />
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {filteredCountries.slice(0, 50).map((country) => (
                  <SelectItem key={country.country_code} value={country.country_code}>
                    <div className="flex items-center gap-2">
                      <span>{country.flag_emoji}</span>
                      <span className="font-medium">{country.country_name_fr}</span>
                      <span className="text-gray-500 text-sm">{country.dial_code}</span>
                    </div>
                  </SelectItem>
                ))}
              </div>
            </SelectContent>
          </Select>
        </div>

        {/* Champ numéro de téléphone */}
        <div className="flex-1">
          <Input
            type="tel"
            value={phoneValue}
            onChange={handlePhoneChange}
            placeholder="Numéro de téléphone"
            required={required}
            disabled={disabled}
            className="w-full"
          />
        </div>
      </div>
      {selectedCountry && (
        <div className="text-xs text-gray-500">
          Format: {selectedCountry.dial_code} suivi du numéro
        </div>
      )}
    </div>
  );
};

export default CountryPhoneSelect;
